import React, { useEffect, useState } from 'react'
import { Alert, Avatar, Box, Button, CircularProgress, Stack, Typography } from '@mui/material'
import AuthSplitCard from './auth/AuthSplitCard'
import Parse from '../services/initializeParse'
import { useAppDispatch } from '../hooks'
import { onboardingReset } from '../stores/OnboardingStore'
import { useRoutingStore } from '../stores/routingStore'
import {
  inviteLinkErrorMessage,
  joinCompanyByInviteLink,
  previewCompanyInviteLink,
  savePendingCompanyInvite,
  type CompanyInviteLinkPreview,
} from '../services/CompanyInviteLinkService'

type Phase = 'loading' | 'ready' | 'joining' | 'error'

/**
 * `/join-company?token=…` (SC-438 D14): shows the company a workspace invite link leads to. A
 * signed-in visitor joins here; a signed-out one signs up or in first, and the token is kept so
 * the onboarding check joins them afterwards.
 */
export default function JoinCompanyPage({ onComplete }: { onComplete: () => void }) {
  const dispatch = useAppDispatch()
  const token = new URLSearchParams(window.location.search).get('token') || ''
  const [phase, setPhase] = useState<Phase>('loading')
  const [preview, setPreview] = useState<CompanyInviteLinkPreview | null>(null)
  const [error, setError] = useState('')
  const signedIn = !!Parse.User.current()

  useEffect(() => {
    let cancelled = false
    previewCompanyInviteLink(token)
      .then((result) => {
        if (cancelled) return
        setPreview(result)
        setPhase('ready')
      })
      .catch((err) => {
        if (cancelled) return
        setError(inviteLinkErrorMessage(err))
        setPhase('error')
      })
    return () => {
      cancelled = true
    }
  }, [token])

  const enterApp = (path: string) => {
    window.history.replaceState({}, document.title, path)
    useRoutingStore.getState().syncWithURL()
    onComplete()
  }

  const join = async () => {
    setPhase('joining')
    setError('')
    try {
      await joinCompanyByInviteLink(token)
      // The wizard was answered before joining; ask again so it opens at "Your Sevv self".
      dispatch(onboardingReset())
      enterApp('/dashboard')
    } catch (err) {
      setError(inviteLinkErrorMessage(err))
      setPhase('ready')
    }
  }

  const signUpFirst = () => {
    savePendingCompanyInvite(token, preview?.companyName ?? '')
    enterApp('/')
  }

  return (
    <AuthSplitCard screen="joinLink">
      <Box sx={{ my: 'auto' }}>
        {phase === 'loading' ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress aria-label="Loading the invite" />
          </Box>
        ) : phase === 'error' || !preview ? (
          <Stack spacing={2}>
            <Typography variant="h5" component="h1" fontWeight={700}>
              This invite link doesn’t work
            </Typography>
            <Alert severity="error">{error || 'This invite link is not valid.'}</Alert>
            <Button variant="outlined" onClick={() => enterApp('/')}>
              Go to Sevv
            </Button>
          </Stack>
        ) : (
          <Stack spacing={2.5} alignItems="center" textAlign="center">
            <Avatar src={preview.logoUrl || undefined} alt="" sx={{ width: 64, height: 64, bgcolor: 'primary.main' }}>
              {preview.companyName.charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="body2" color="text.secondary">
                You’re invited to join
              </Typography>
              <Typography variant="h5" component="h1" fontWeight={700} data-testid="join-company-name">
                {preview.companyName}
              </Typography>
            </Box>
            {error && (
              <Alert severity="error" sx={{ alignSelf: 'stretch', textAlign: 'left' }}>
                {error}
              </Alert>
            )}
            {signedIn ? (
              <Button variant="contained" size="large" fullWidth onClick={join} disabled={phase === 'joining'}>
                {phase === 'joining' ? 'Joining…' : `Join ${preview.companyName}`}
              </Button>
            ) : (
              <Button variant="contained" size="large" fullWidth onClick={signUpFirst}>
                Sign up or sign in to join
              </Button>
            )}
          </Stack>
        )}
      </Box>
    </AuthSplitCard>
  )
}
